import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Spinner,
  Button,
  Stack,
  Input,
} from '@chakra-ui/react'
import { CheckCircle, XCircle, Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Field } from '@/components/ui/field'
import { PasswordStrengthIndicator } from '@/shared/components/PasswordStrengthIndicator'

type AccountCreationStatus = 'loading' | 'form' | 'processing' | 'success' | 'error' | 'redirect_to_login'

interface StripeSessionData {
  sessionId: string
  paymentStatus: string
  subscription: {
    id: string
    status: string
    currentPeriodStart: number
    currentPeriodEnd: number
    items: Array<{
      id: string
      priceId: string
      productId: string
      quantity: number
    }>
  } | null
  customer: {
    id: string | null
    email: string
    name: string | null
    phone: string | null
  }
  payment: {
    amount: number
    currency: string
    status: string
  } | null
  metadata: Record<string, string>
}

export function AccountCreationPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<AccountCreationStatus>('loading')
  const [error, setError] = useState<string>('')
  const [sessionData, setSessionData] = useState<StripeSessionData | null>(null)

  // Form states
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [agencyName, setAgencyName] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    const sessionId = searchParams.get('CSID')

    if (!sessionId) {
      setError('Session ID manquant. Veuillez vérifier le lien de confirmation.')
      setStatus('error')
      return
    }

    retrieveStripeSession(sessionId)
  }, [searchParams])

  // Parse full name into first name and last name
  const parseFullName = (fullName: string): { firstName: string; lastName: string } => {
    if (!fullName) return { firstName: '', lastName: '' }

    const parts = fullName.trim().split(' ').filter(p => p.length > 0)

    if (parts.length === 0) {
      return { firstName: '', lastName: '' }
    }

    if (parts.length === 1) {
      return { firstName: parts[0] ?? '', lastName: '' }
    }

    if (parts.length === 2) {
      return { firstName: parts[0] ?? '', lastName: (parts[1] ?? '').toUpperCase() }
    }

    // For names with 3+ parts, check for particles (de, du, van, von, etc.)
    const particles = ['de', 'du', 'des', 'van', 'von', 'la', 'le']
    const lastWordIndex = parts.length - 1

    // If second to last word is a particle, it's part of the last name
    const secondToLastWord = parts[lastWordIndex - 1]
    if (secondToLastWord && particles.includes(secondToLastWord.toLowerCase())) {
      const firstName = parts.slice(0, lastWordIndex - 1).join(' ')
      const lastName = parts.slice(lastWordIndex - 1).join(' ').toUpperCase()
      return { firstName, lastName }
    }

    // Otherwise, first word is first name, rest is last name
    const firstName = parts[0] ?? ''
    const lastName = parts.slice(1).join(' ').toUpperCase()
    return { firstName, lastName }
  }

  const retrieveStripeSession = async (sessionId: string) => {
    try {
      setStatus('loading')

      // Retrieve Stripe session details via Edge Function
      console.log('📝 Retrieving Stripe session:', sessionId)

      const { data: functionData, error: functionError } = await supabase.functions.invoke(
        'retrieve-stripe-session',
        {
          body: { sessionId },
        }
      )

      if (functionError) {
        throw new Error(`Failed to retrieve session: ${functionError.message}`)
      }

      if (!functionData) {
        throw new Error('No session data returned')
      }

      console.log('✅ Session data retrieved:', functionData)
      console.log('📊 FULL STRIPE SESSION DATA (JSON):', JSON.stringify(functionData, null, 2))
      setSessionData(functionData)

      // Check payment status
      if (functionData.paymentStatus !== 'paid') {
        throw new Error('Payment not completed. Please contact support.')
      }

      // Check if user with this email already exists in auth.users (source of truth)
      console.log('🔍 Checking if user already exists in auth.users:', functionData.customer.email)
      const { data: userExists, error: checkUserError } = await supabase
        .rpc('check_user_exists_by_email', { user_email: functionData.customer.email })

      if (checkUserError) {
        console.error('Error checking existing user:', checkUserError)
        throw new Error('Erreur lors de la vérification du compte')
      }

      if (userExists) {
        console.log('⚠️ User already exists in auth.users')
        // User already exists, show message with login CTA
        setStatus('redirect_to_login')
        return
      }

      console.log('✅ No existing user found, proceeding with account creation')

      // Parse and pre-fill name from Stripe customer
      if (functionData.customer.name) {
        const { firstName: parsedFirstName, lastName: parsedLastName } = parseFullName(functionData.customer.name)
        setFirstName(parsedFirstName)
        setLastName(parsedLastName)
      }

      // Pre-fill agency name with customer name if available
      const defaultAgencyName = functionData.customer.name || functionData.customer.email.split('@')[0]
      setAgencyName(defaultAgencyName)

      // Pre-fill phone if available from Stripe
      if (functionData.customer.phone) {
        setPhone(functionData.customer.phone)
      }

      // Show form for user to configure their account
      setStatus('form')

    } catch (err) {
      console.error('Error retrieving session:', err)
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
      setStatus('error')
    }
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    // First name validation
    if (!firstName.trim()) {
      errors.firstName = 'Le prénom est requis'
    } else if (firstName.trim().length < 2) {
      errors.firstName = 'Le prénom doit contenir au moins 2 caractères'
    }

    // Last name validation
    if (!lastName.trim()) {
      errors.lastName = 'Le nom est requis'
    } else if (lastName.trim().length < 2) {
      errors.lastName = 'Le nom doit contenir au moins 2 caractères'
    }

    // Phone validation (optional, but if provided must be valid)
    if (phone.trim()) {
      // Basic phone validation: at least 10 digits
      const phoneDigits = phone.replace(/\D/g, '')
      if (phoneDigits.length < 10) {
        errors.phone = 'Le numéro de téléphone doit contenir au moins 10 chiffres'
      }
    }

    // Password validation
    if (!password) {
      errors.password = 'Le mot de passe est requis'
    } else if (password.length < 12) {
      errors.password = 'Le mot de passe doit contenir au moins 12 caractères'
    } else if (!/[A-Z]/.test(password)) {
      errors.password = 'Le mot de passe doit contenir au moins une majuscule'
    } else if (!/[a-z]/.test(password)) {
      errors.password = 'Le mot de passe doit contenir au moins une minuscule'
    } else if (!/[0-9]/.test(password)) {
      errors.password = 'Le mot de passe doit contenir au moins un chiffre'
    } else if (!/[^A-Za-z0-9]/.test(password)) {
      errors.password = 'Le mot de passe doit contenir au moins un caractère spécial'
    }

    // Confirm password validation
    if (!confirmPassword) {
      errors.confirmPassword = 'Veuillez confirmer votre mot de passe'
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas'
    }

    // Agency name validation
    if (!agencyName.trim()) {
      errors.agencyName = 'Le nom de l\'agence est requis'
    } else if (agencyName.trim().length < 2) {
      errors.agencyName = 'Le nom de l\'agence doit contenir au moins 2 caractères'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm() || !sessionData) {
      return
    }

    try {
      setStatus('processing')
      console.log('👤 Creating user account for:', sessionData.customer.email)

      // Step 1: Create user account in auth with chosen password and metadata
      const fullName = `${firstName.trim()} ${lastName.trim()}`
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: sessionData.customer.email,
        password: password, // Use the password chosen by the user
        options: {
          emailRedirectTo: `${window.location.origin}/app/dashboard`,
          data: {
            full_name: fullName,
            phone: phone.trim() || null,
            stripe_customer_id: sessionData.customer.id,
          },
        },
      })

      if (signUpError) {
        // If user already exists, try to sign in instead
        if (signUpError.message.includes('already registered')) {
          console.log('ℹ️ User already exists, updating subscription info')
          // In this case, we should update the user's subscription
          // This would require the user to be logged in or use a different flow
          throw new Error('Un compte existe déjà avec cet email. Veuillez vous connecter.')
        }
        throw signUpError
      }

      if (!authData.user) {
        throw new Error('Failed to create user account')
      }

      console.log('✅ User account created:', authData.user.id)

      // Auto-confirm email for users coming from Stripe (payment verified)
      // This bypasses email confirmation since payment = identity verification
      const { error: confirmError } = await supabase.rpc('confirm_user_email', {
        user_id: authData.user.id
      })

      if (confirmError) {
        console.error('Failed to confirm email:', confirmError)
        throw new Error('Erreur lors de la confirmation de l\'email')
      }

      console.log('✅ Email auto-confirmed (Stripe verified)')

      // CRITICAL: Sign in with password to create an authenticated session
      // signUp() doesn't create a session if email isn't confirmed, so we need to sign in
      // now that we've confirmed the email in the database
      console.log('🔑 Signing in to create authenticated session...')
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: sessionData.customer.email,
        password: password,
      })

      if (signInError) {
        console.error('Failed to sign in:', signInError)
        throw new Error('Erreur lors de la connexion')
      }

      console.log('✅ Signed in successfully, user is now authenticated')

      // Step 2: Create agency and setup user profile in one atomic transaction
      // This uses a PostgreSQL function that handles all the logic with proper security
      console.log('🏢 Creating agency and setting up profile...')

      // Generate a clean slug from the agency name (no random characters)
      const slug = agencyName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')

      // Extract Stripe subscription data
      const subscriptionItem = sessionData.subscription?.items?.[0]
      const stripeCustomerId = sessionData.customer.id ?? ''
      const stripeSubscriptionId = sessionData.subscription?.id ?? ''
      const stripeSubscriptionItemId = subscriptionItem?.id ?? ''
      const stripePriceId = subscriptionItem?.priceId ?? ''
      const stripeProductId = subscriptionItem?.productId ?? ''
      const subscriptionStatus = sessionData.subscription?.status ?? 'active'
      const subscriptionQuantity = subscriptionItem?.quantity ?? 1

      // Convert Unix timestamps to ISO dates (Stripe uses seconds, JS uses milliseconds)
      const periodStart = sessionData.subscription?.currentPeriodStart
        ? new Date(sessionData.subscription.currentPeriodStart * 1000).toISOString()
        : ''
      const periodEnd = sessionData.subscription?.currentPeriodEnd
        ? new Date(sessionData.subscription.currentPeriodEnd * 1000).toISOString()
        : ''

      console.log('📦 Stripe data:', {
        stripeCustomerId,
        stripeSubscriptionId,
        stripeSubscriptionItemId,
        stripePriceId,
        stripeProductId,
        subscriptionStatus,
        subscriptionQuantity,
        periodStart,
        periodEnd,
      })

      const { data: agencyData, error: agencyError } = await supabase
        .rpc('create_agency_for_new_user', {
          p_user_id: authData.user.id,
          p_agency_name: agencyName.trim(),
          p_agency_slug: slug,
          p_first_name: firstName.trim(),
          p_last_name: lastName.trim().toUpperCase(),
          p_phone: phone.trim() || '',
          p_stripe_customer_id: stripeCustomerId,
          p_stripe_subscription_id: stripeSubscriptionId,
          p_stripe_subscription_item_id: stripeSubscriptionItemId,
          p_stripe_price_id: stripePriceId,
          p_stripe_product_id: stripeProductId,
          p_subscription_status: subscriptionStatus,
          p_subscription_quantity: subscriptionQuantity,
          p_subscription_period_start: periodStart,
          p_subscription_period_end: periodEnd,
          p_trial_end: null as any, // Will be calculated in the function if null (now + 7 days)
        } as any)

      if (agencyError) {
        console.error('Failed to create agency:', agencyError)
        throw new Error('Erreur lors de la création de l\'agence')
      }

      console.log('✅ Agency created and user profile updated:', agencyData)

      // Success!
      setStatus('success')

      // Send welcome email with password reset link
      // This will be handled by Supabase Auth automatically

      // Redirect to app after 3 seconds
      setTimeout(() => {
        navigate('/app/onboarding')
      }, 3000)

    } catch (err) {
      console.error('Error during account creation:', err)
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
      setStatus('error')
    }
  }

  const retrySessionRetrieval = () => {
    const sessionId = searchParams.get('CSID')
    if (sessionId) {
      retrieveStripeSession(sessionId)
    }
  }

  return (
    <Box bg={"white"} color={"gray.900"} minH="100vh" display="flex" alignItems="center">
      {/* Background Pattern */}
      <Box
        position="fixed"
        inset={0}
        bg="radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.1), transparent 70%)"
        pointerEvents="none"
      />
      <Box
        position="fixed"
        inset={0}
        bgImage="linear-gradient(to right, rgba(16, 185, 129, 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(16, 185, 129, 0.05) 1px, transparent 1px)"
        bgSize="60px 60px"
        opacity={0.3}
        pointerEvents="none"
      />

      <Container maxW="700px" position="relative" zIndex={1} py={20}>
        <Box
          bg={"white"}
          borderRadius="2xl"
          border="1px solid"
          borderColor={"gray.200"}
          p={8}
        >
          <VStack gap={8} align="stretch">
            {/* Loading State */}
            {status === 'loading' && (
              <VStack gap={6} textAlign="center">
                <Box
                  w="80px"
                  h="80px"
                  borderRadius="full"
                  bg="brand.100"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Spinner size="xl" color="brand.600" />
                </Box>
                <Heading as="h1" size="xl">
                  Vérification du paiement
                </Heading>
                <Text color={"gray.600"} fontSize="lg">
                  Nous vérifions votre paiement auprès de Stripe...
                </Text>
              </VStack>
            )}

            {/* Form State - Configure account */}
            {status === 'form' && sessionData && (
              <>
                <Box>
                  <Heading as="h1" size="xl" mb={2}>
                    Configurez votre compte
                  </Heading>
                  <Text color={"gray.600"} fontSize="md">
                    Complétez les informations ci-dessous pour finaliser la création de votre compte
                  </Text>
                </Box>

                <Box
                  as="form"
                  onSubmit={handleFormSubmit}
                  w="full"
                  maxW="600px"
                >
                  <VStack gap={6} align="stretch">
                    {/* Email (disabled, from Stripe) */}
                    <Field label="Adresse email" required>
                      <Input
                        type="email"
                        value={sessionData.customer.email}
                        disabled
                        opacity={0.7}
                        cursor="not-allowed"
                        size="lg"
                      />
                    </Field>

                    {/* First Name */}
                    <Field
                      label="Prénom"
                      required
                      error={formErrors.firstName}
                    >
                      <Input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Jean"
                        size="lg"
                      />
                    </Field>

                    {/* Last Name */}
                    <Field
                      label="Nom"
                      required
                      error={formErrors.lastName}
                    >
                      <Input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value.toUpperCase())}
                        placeholder="DUPONT"
                        size="lg"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </Field>

                    {/* Phone */}
                    <Field
                      label="Téléphone"
                      error={formErrors.phone}
                      helperText="Optionnel"
                    >
                      <Input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+33 6 12 34 56 78"
                        size="lg"
                      />
                    </Field>

                    {/* Password */}
                    <Field
                      label="Mot de passe"
                      required
                      error={formErrors.password}
                      helperText="Minimum 12 caractères, incluant majuscule, minuscule, chiffre et caractère spécial"
                    >
                      <Box position="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••••••"
                          size="lg"
                          pr="12"
                        />
                        <Button
                          onClick={() => setShowPassword(!showPassword)}
                          variant="ghost"
                          position="absolute"
                          right={2}
                          top="50%"
                          transform="translateY(-50%)"
                          size="sm"
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </Button>
                      </Box>
                      {password && <PasswordStrengthIndicator password={password} />}
                    </Field>

                    {/* Confirm Password */}
                    <Field
                      label="Confirmer le mot de passe"
                      required
                      error={formErrors.confirmPassword}
                    >
                      <Box position="relative">
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="••••••••••••"
                          pr="12"
                          size="lg"
                        />
                        <Button
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          variant="ghost"
                          position="absolute"
                          right={2}
                          top="50%"
                          transform="translateY(-50%)"
                          size="sm"
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </Button>
                      </Box>
                    </Field>

                    {/* Agency Name */}
                    <Field
                      label="Nom de votre agence"
                      required
                      error={formErrors.agencyName}
                      helperText="Ce nom sera affiché dans l'interface"
                    >
                      <Input
                        type="text"
                        value={agencyName}
                        onChange={(e) => setAgencyName(e.target.value)}
                        placeholder="Mon Agence Immobilière"
                        size="lg"
                      />
                    </Field>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      size="lg"
                      colorPalette="brand"
                      bg="brand.600"
                      color="white"
                      _hover={{ bg: 'brand.700' }}
                      w="full"
                      mt={4}
                    >
                      Créer mon compte
                    </Button>
                  </VStack>
                </Box>
              </>
            )}

            {/* Processing State */}
            {status === 'processing' && (
              <VStack gap={6} textAlign="center">
                <Box
                  w="80px"
                  h="80px"
                  borderRadius="full"
                  bg="brand.100"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Loader2 size={40} color="#10b981" className="animate-spin" />
                </Box>
                <Heading as="h1" size="xl">
                  Création de votre compte
                </Heading>
                <Text color={"gray.600"} fontSize="lg">
                  Configuration de votre espace de travail...
                </Text>
                {sessionData && (
                  <VStack gap={2} w="full" align="start" p={4} bg={"gray.50"} borderRadius="lg">
                    <Text fontSize="sm" color={"gray.600"}>
                      <strong>Email:</strong> {sessionData.customer.email}
                    </Text>
                    {sessionData.subscription && (
                      <Text fontSize="sm" color={"gray.600"}>
                        <strong>Formule:</strong> {sessionData.subscription.items[0]?.productId}
                      </Text>
                    )}
                  </VStack>
                )}
              </VStack>
            )}

            {/* Success State */}
            {status === 'success' && (
              <VStack gap={6} textAlign="center">
                <Box
                  w="80px"
                  h="80px"
                  borderRadius="full"
                  bg="brand.100"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <CheckCircle size={40} color="#10b981" />
                </Box>
                <Heading as="h1" size="xl">
                  Compte créé avec succès !
                </Heading>
                <Text color={"gray.600"} fontSize="lg">
                  Votre compte a été créé et votre abonnement est actif.
                </Text>
                <VStack gap={3} w="full" align="start" p={4} bg={"gray.50"} borderRadius="lg">
                  <Text fontSize="sm">
                    Un email de confirmation a été envoyé à <strong>{sessionData?.customer.email}</strong>
                  </Text>
                  <Text fontSize="sm" color={"gray.600"}>
                    Vous allez être redirigé vers votre espace en quelques secondes...
                  </Text>
                </VStack>
                <Button
                  onClick={() => navigate('/app/onboarding')}
                  size="lg"
                  colorPalette="brand"
                  bg="brand.600"
                  color="white"
                  _hover={{ bg: 'brand.700' }}
                  w="full"
                >
                  Accéder à mon espace
                </Button>
              </VStack>
            )}

            {/* Redirect to Login State - Account Already Exists */}
            {status === 'redirect_to_login' && (
              <VStack gap={6} textAlign="center">
                <Box
                  w="80px"
                  h="80px"
                  borderRadius="full"
                  bg="blue.500/10"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <AlertCircle size={40} color="#3b82f6" />
                </Box>
                <Heading as="h1" size="xl">
                  Compte existant
                </Heading>
                <Text color={"gray.600"} fontSize="lg">
                  Un compte existe déjà avec l'adresse email <strong>{sessionData?.customer.email}</strong>
                </Text>
                <VStack gap={3} w="full" align="start" p={4} bg={"blue.50"} borderRadius="lg" borderLeft="4px solid" borderColor="blue.500">
                  <Text fontSize="sm" color={"gray.700"}>
                    Votre paiement a bien été enregistré et votre abonnement est actif.
                  </Text>
                  <Text fontSize="sm" color={"gray.700"}>
                    Veuillez vous connecter avec votre compte existant.
                  </Text>
                </VStack>
                <Button
                  onClick={() => navigate('/app/login?message=account_exists')}
                  size="lg"
                  colorPalette="blue"
                  bg="blue.600"
                  color="white"
                  _hover={{ bg: 'blue.700' }}
                  w="full"
                >
                  Se connecter maintenant
                </Button>
              </VStack>
            )}

            {/* Error State */}
            {status === 'error' && (
              <VStack gap={6} textAlign="center">
                <Box
                  w="80px"
                  h="80px"
                  borderRadius="full"
                  bg="red.500/10"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <XCircle size={40} color="#ef4444" />
                </Box>
                <Heading as="h1" size="xl">
                  Erreur lors de la création
                </Heading>
                <Text color={"gray.600"} fontSize="lg">
                  {error || 'Une erreur est survenue lors de la création de votre compte.'}
                </Text>
                <Stack direction={{ base: 'column', sm: 'row' }} gap={4} w="full">
                  <Button
                    onClick={retrySessionRetrieval}
                    size="lg"
                    colorPalette="brand"
                    bg="brand.600"
                    color="white"
                    _hover={{ bg: 'brand.700' }}
                    flex={1}
                  >
                    Réessayer
                  </Button>
                  <Button
                    onClick={() => navigate('/contact')}
                    size="lg"
                    variant="outline"
                    borderColor={"gray.200"}
                    flex={1}
                  >
                    Contacter le support
                  </Button>
                </Stack>
              </VStack>
            )}
          </VStack>
        </Box>
      </Container>
    </Box>
  )
}
